/**
 * Imagine AI — 6-scene narrative demo.
 *
 * A story. Not a screen-flipper.
 *
 * Maya, a PM at a B2B SaaS support platform, is about to brief engineering
 * on an AI feature. She runs it through Imagine AI first. What she finds
 * changes the spec — but doesn't kill the feature.
 *
 * Scene durations are per-scene; the player reads them for autoplay.
 */

export const MAYA = {
  name: "Maya",
  role: "Senior PM",
  company: "B2B SaaS support platform",
  context:
    "Their platform is used by ~500 support agents across 30 mid-market companies. ~2,000 tickets a day.",
  featureBrief:
    "AI-generated draft replies. The agent reviews and edits before sending. ~60% of tickets would get a draft (~1,200/day).",
  rough_estimate: "$300/month",
  rough_estimate_label: "in tokens",
};

// Scene 4 — the two sharp questions
export const DEMO_QUESTIONS = [
  {
    assistant: "What does a wrong draft cost in agent rework time?",
    options: [
      "Annoyance only",
      "Refund or rework ($25)",
      "Operational impact",
      "Regulated / safety",
    ],
    answered: "Refund or rework ($25)",
  },
  {
    assistant: "Who reviews the AI's output before it's sent?",
    options: [
      "No human review",
      "Sampled QA (5%)",
      "Low-confidence only (10%)",
      "Every output reviewed",
    ],
    answered: "Every output reviewed",
  },
];

// Scene 5 — the flow + the picture (verified deterministic numbers from a real live run)
export const DEMO_FLOW = [
  { step: 1, label: "Ticket arrives", sublabel: "Inbox event", type: "local", cost_per_call: "Free" },
  { step: 2, label: "Pull history", sublabel: "Database lookup", type: "local", cost_per_call: "Free" },
  { step: 3, label: "Categorise urgency", sublabel: "Local rules", type: "local", cost_per_call: "Free" },
  { step: 4, label: "Match KB article", sublabel: "Cacheable lookup", type: "cacheable", cost_per_call: "Near-free" },
  { step: 5, label: "Draft reply", sublabel: "API required", type: "api", cost_per_call: "$0.011/call" },
  { step: 6, label: "Agent reviews", sublabel: "Human review", type: "local", cost_per_call: "—" },
  { step: 7, label: "Send", sublabel: "Local UI", type: "local", cost_per_call: "Free" },
];

export const DEMO_DECISION = {
  headline: "Build with caveats.",
  body: "Inference is cheap. The hidden cost is human review and wrong-output exposure at scale.",
};

// Climax numbers — verified live from /api/sessions/.../analyze run
export const DEMO_MOMENT = {
  inference_usd: 411,
  review_usd: 60000,
  error_usd: 90000,
  value_floor_usd: 150411,
  annual_floor_usd: 1804932,
  dominant_share_pct: 60,
  token_share_pct: 0.3,
};

// Scene 6 — Maya's resolution
export const DEMO_RESOLUTION = {
  before: {
    spec: "Every ticket gets an AI draft. Every draft reviewed.",
    floor: 150411,
    annualFloor: 1804932,
  },
  after: {
    spec: "Only high-confidence tickets get a suggestion. Agent reviews as part of normal flow.",
    floor: 2826,
    annualFloor: 33912,
  },
};

// Stepper metadata + per-scene autoplay durations.
export const DEMO_SCREENS = [
  { key: "open", title: "Meet Maya", durationMs: 8000 },
  { key: "assumption", title: "The estimate", durationMs: 8000 },
  { key: "context", title: "The context", durationMs: 10000 },
  { key: "questions", title: "The questions", durationMs: 14000 },
  { key: "moment", title: "The picture", durationMs: 22000 },
  { key: "resolution", title: "The decision", durationMs: 10000 },
];
